import React, { FC, useRef } from 'react';

import styled, { css } from 'styled-components';
import { IMaskMixin } from 'react-imask';

import Input, { SeturInputProps, InputApi } from '@components/Input';
import type { InputVariant } from '@components/Input/InputProps';
import { IOS_INPUT_ZOOM_GUARD } from '@components/Input/variants/mixins';
import { useHeight } from '@components/utils/hooks/useSize';

const FORM_INPUT_TYPOGRAPHY = css`
  color: ${({ theme }) => theme.palette.grey1100};
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;

  &::placeholder {
    color: ${({ theme }) => theme.palette.inputPlaceholderColor};
  }
`;

export const BaseInput = styled.input.withConfig({
  shouldForwardProp: (prop) => (prop as string) !== 'hasError',
})<{ hasError?: boolean; $inputVariant?: InputVariant }>`
  &:focus {
    top: 8px;
  }
  width: 100%;
  height: 42px;
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.palette.errorColor : theme.palette.grey200};
  border-radius: 4px;
  &:focus {
    border: 1px solid
      ${({ theme, hasError }) =>
        hasError ? theme.palette.errorColor : theme.palette.grey800};
  }
  ${({ $inputVariant }) =>
    $inputVariant === 'form' ? FORM_INPUT_TYPOGRAPHY : null}
  ${IOS_INPUT_ZOOM_GUARD}
`;
export const Container = styled.div<{ disabled?: boolean }>`
  display: flex;
  position: relative;

  & > div {
    flex: 1;
  }
  & > span {
    position: absolute;
    right: 16px;
    bottom: 0;
    top: 0;
    margin: auto;
    height: 16px;
    opacity: ${(p) => (p.disabled ? 0.4 : 1)};
  }
`;

type FormattedInputConfig = {
  blocks: unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mask: any;
  shouldPreventChar?: (char: string) => boolean;
  placeholder?: string;
  name?: string;
  inputVariant?: InputVariant;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & { [key: string]: any };

export const FormattedInputBuilder: (config: FormattedInputConfig) => FC<
  SeturInputProps & {
    forwardDefaultValue?: string;
    nativeInputRef?: React.MutableRefObject<HTMLInputElement>;
  }
> =
  ({
    blocks,
    mask,
    shouldPreventChar = () => false,
    name,
    placeholder,
    inputVariant,
    ...opts
  }) =>
  ({ icon, disabled, getInputAPI, nativeInputRef, ...rest }) => {
    const {
      value: forwardValue,
      defaultValue: forwardDefaultValue,
      ...restProps
    } = rest;

    return (
      <Container disabled={disabled}>
        <FormattedInputWrapper
          mask={mask}
          blocks={blocks}
          unmask={false}
          name={name}
          type='text'
          InputComponent={BaseInput}
          disabled={disabled}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (shouldPreventChar?.(e.key)) e.preventDefault();
          }}
          forwardValue={forwardValue}
          forwardDefaultValue={forwardDefaultValue}
          inputRef={nativeInputRef}
          getInputAPI={(api: InputApi) => getInputAPI?.(api, nativeInputRef)}
          $inputVariant={inputVariant}
          {...opts}
          {...restProps}
        />
        {icon}
      </Container>
    );
  };

export const FormattedInputWrapper = IMaskMixin<
  SeturInputProps & {
    InputComponent: React.ComponentType;
    forwardValue?: SeturInputProps['value'];
    forwardDefaultValue?: SeturInputProps['value'];
    $inputVariant?: InputVariant;
  },
  unknown
>(
  ({
    InputComponent,
    forwardValue,
    forwardDefaultValue,
    getInputAPI,
    ...rest
  }) => {
    const height = useHeight();
    const values = {
      value: forwardValue,
      defaultValue: forwardDefaultValue as
        | string
        | number
        | readonly string[]
        | undefined,
    };

    const numericInputRef = useRef<InputApi>(null);

    return (
      <Input
        {...rest}
        {...values}
        InputComponent={InputComponent}
        height={height}
        onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
          const value = e.clipboardData.getData('text');
          numericInputRef.current?.applyValue(value);
          rest.onChange?.(value);
        }}
        onInput={(e) => {
          const value = (e.target as HTMLInputElement).value;
          numericInputRef.current?.applyValue(value);
          rest.onChange?.(value);
        }}
        getInputAPI={(numericInputApi: InputApi) => {
          numericInputRef.current = numericInputApi;
          getInputAPI?.(numericInputApi);
        }}
        inputMode='numeric'
      />
    );
  }
);
